"use client";

import { usePersonalDataActions } from "@/features/profile";
import { useUserProfile } from "@/state/globalStore";
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import SettingsSkeleton from "./SettingsSkeleton";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericToggle from "@/shared/ui/controls/GenericToggle";
import GenericEditable from "@/shared/ui/controls/GenericEditable";

import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import * as z from "zod";
import type { SupportedLanguage, PersonalData } from "@/generated/fastapi";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";

// ---------------------------------
// Utilities & Validation
// ---------------------------------

const emailSchema = z.string().email("Please enter a valid email address");

type PersonalLinkKind = "linkedin" | "github";
type PersonalLinkResult =
  | { ok: true; value: string; displayValue: string }
  | { ok: false; message: string };

const GITHUB_USERNAME_RE =
  /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,37}[A-Za-z0-9])?$/;

function displayPersonalLink(value: string) {
  const parsed = new URL(value);
  return `${parsed.hostname.replace(/^www\./, "")}${parsed.pathname}`.replace(
    /\/$/,
    "",
  );
}

function normalizePersonalLink(
  value: string | null | undefined,
  kind: PersonalLinkKind,
): PersonalLinkResult {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return { ok: true, value: "", displayValue: "" };

  if (/\s/.test(trimmed)) {
    return { ok: false, message: "Links cannot contain spaces." };
  }

  if (kind === "github") {
    const githubHandle = trimmed.replace(/^@/, "");
    if (GITHUB_USERNAME_RE.test(githubHandle)) {
      const normalized = `https://github.com/${githubHandle}`;
      return {
        ok: true,
        value: normalized,
        displayValue: displayPersonalLink(normalized),
      };
    }
  }

  const urlToParse = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const parsed = new URL(urlToParse);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const pathParts = parsed.pathname.split("/").filter(Boolean);

    if (kind === "linkedin") {
      if (host !== "linkedin.com") {
        return { ok: false, message: "Enter a LinkedIn profile URL." };
      }
      if (pathParts.length !== 2 || pathParts[0] !== "in") {
        return {
          ok: false,
          message: "Enter a LinkedIn /in/ profile URL.",
        };
      }
      const normalized = `https://linkedin.com/in/${pathParts[1]}`;
      return {
        ok: true,
        value: normalized,
        displayValue: displayPersonalLink(normalized),
      };
    }

    if (host !== "github.com") {
      return { ok: false, message: "Enter a GitHub profile URL." };
    }
    if (pathParts.length !== 1 || !GITHUB_USERNAME_RE.test(pathParts[0])) {
      return {
        ok: false,
        message: "Enter a GitHub profile URL or username.",
      };
    }
    const normalized = `https://github.com/${pathParts[0]}`;
    return {
      ok: true,
      value: normalized,
      displayValue: displayPersonalLink(normalized),
    };
  } catch {
    return { ok: false, message: "Enter a valid profile URL." };
  }
}

function getCurrentLanguageConfig(
  personalData: PersonalData,
  language: SupportedLanguage,
) {
  return {
    claim: personalData.claim[language] || { text: null, enabled: false },
    intro: personalData.intro[language] || { text: null, enabled: false },
  };
}

// ---------------------------------
// Component: PersonalData
// ---------------------------------

export default function PersonalData() {
  const { languageItems } = useOptionItems();
  const { userProfile } = useUserProfile();
  const { updatePersonalDataSync, resetPersonalDataSync } =
    usePersonalDataActions();
  const currentPersonalData = userProfile.data.personal_data;
  const currentPrintLanguage = userProfile.data.print_settings.print_language;

  const [activeLanguages, setActiveLanguages] = useState<SupportedLanguage[]>([
    currentPrintLanguage,
  ]);
  const activeLanguage = activeLanguages[0];

  // Local state for immediate UI feedback
  const [personalData, setPersonalData] =
    useState<PersonalData>(currentPersonalData);
  const [resetKey, setResetKey] = useState(0);

  // Validation errors
  const [emailError, setEmailError] = useState<string | null>(null);
  const [linkedinUrlError, setLinkedinUrlError] = useState<string | null>(null);
  const [githubUrlError, setGithubUrlError] = useState<string | null>(null);

  // Local state: lock/unlock active language selector
  const [languageSelectorLocked, setLanguageSelectorLocked] = useState(true);

  if (userProfile.isLoading) {
    return <SettingsSkeleton />;
  }

  const selectedLanguageConfig = getCurrentLanguageConfig(
    personalData,
    activeLanguage,
  );
  const activeLanguageLabel =
    languageItems.find((item) => item.value === activeLanguage)?.label ??
    activeLanguage.toUpperCase();
  const enabledPersonalItems = [
    personalData.first_name.enabled,
    personalData.last_name.enabled,
    personalData.email.enabled,
    personalData.phone.enabled,
    personalData.address.enabled,
    personalData.linkedin_url.enabled,
    personalData.github_url.enabled,
    selectedLanguageConfig.claim.enabled,
    selectedLanguageConfig.intro.enabled,
  ].filter(Boolean).length;

  // Generic update handler
  async function handleUpdate(updates: Partial<PersonalData>) {
    setPersonalData((prev) => ({ ...prev, ...updates }));
    const updatedData = await updatePersonalDataSync(updates);
    if (updatedData) {
      setPersonalData(updatedData);
      setResetKey((prev) => prev + 1);
    }
  }

  // Reset handler
  async function handleReset() {
    const resetData = await resetPersonalDataSync();
    if (resetData) {
      setPersonalData(resetData);
      setResetKey((prev) => prev + 1);
      setEmailError(null);
      setLinkedinUrlError(null);
      setGithubUrlError(null);
    }
  }

  // Keyboard handler
  function handleKeyDown(e: React.KeyboardEvent, submitFunction: () => void) {
    if (e.key === "Enter") {
      e.preventDefault();
      submitFunction();
    }
  }

  // Validation functions
  const validateEmail = (value: string): boolean => {
    if (!value) return true;
    try {
      emailSchema.parse(value);
      setEmailError(null);
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setEmailError(error.errors[0].message);
      } else {
        setEmailError("Invalid email format");
      }
      return false;
    }
  };

  const validatePersonalLink = (
    value: string,
    type: PersonalLinkKind,
  ): PersonalLinkResult => {
    const result = normalizePersonalLink(value, type);
    if (type === "linkedin") {
      setLinkedinUrlError(result.ok ? null : result.message);
    }
    if (type === "github") {
      setGithubUrlError(result.ok ? null : result.message);
    }
    return result;
  };

  const linkedinPreview = normalizePersonalLink(
    personalData.linkedin_url.text,
    "linkedin",
  );
  const githubPreview = normalizePersonalLink(
    personalData.github_url.text,
    "github",
  );

  return (
    <VStack align="stretch" spaceY={4}>
      <GenericDrawerInfo
        description="Choose which personal details can appear in generated profile exports. Text fields save when you confirm each edit."
        items={[
          {
            label: personalData.allow_print
              ? "Personal data printable"
              : "Personal data hidden",
            colorPalette: personalData.allow_print ? "green" : "gray",
          },
          { label: activeLanguageLabel, colorPalette: "teal" },
          {
            label: `${enabledPersonalItems} enabled`,
            colorPalette: enabledPersonalItems > 0 ? "blue" : "gray",
          },
        ]}
      />

      {/* Control section */}
      <HStack justify="space-between" align="center" gap="2">
        <HStack
          flex="1"
          minW="0"
          gap="2"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          p="1"
        >
          <Box flex="1" minW="0" overflow="hidden">
            <GenericSelector<SupportedLanguage>
              previewItems={languageItems}
              stateObject={activeLanguages}
              stateObjectSetter={setActiveLanguages}
              disableClear={true}
              disabled={languageSelectorLocked}
              suppressDefaultStyles={true}
            />
          </Box>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setLanguageSelectorLocked((isLocked) => !isLocked)}
            aria-label={
              languageSelectorLocked
                ? "Unlock language selector"
                : "Lock language selector"
            }
            minW={{ base: "10", sm: "auto" }}
          >
            <GenericIcon icon={languageSelectorLocked ? "lock" : "unlock"} />
            <Text as="span" display={{ base: "none", sm: "inline" }}>
              {languageSelectorLocked ? "Unlock" : "Lock"}
            </Text>
          </Button>
        </HStack>
        <WithLongPress
          onLongPress={handleReset}
          enabled={true}
          longPressHint="Hold to reset personal data"
          threshold="critical"
        >
          <Button
            variant="ghost"
            // onClick={handleReset} // Disabled in favor of long press
            color="moveon.danger"
          >
            <GenericIcon icon="reset" />
            Reset
          </Button>
        </WithLongPress>
      </HStack>

      {/* Global Toggle */}
      <GenericToggle
        key={`allow-print-toggle-${personalData.allow_print}`}
        label="Allow Printing"
        description="Controls whether personal data appears in printed profile exports."
        value={personalData.allow_print || false}
        onChange={(newValue) => handleUpdate({ allow_print: newValue })}
        minW="48"
      />

      {/* First Name */}
      <GenericToggle
        key={`first-name-toggle-${personalData.first_name.enabled}`}
        label="First Name"
        value={personalData.first_name.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            first_name: { ...personalData.first_name, enabled },
          })
        }
        minW="48"
      />
      {personalData.first_name.enabled && (
        <GenericEditable
          key={`first-name-${resetKey}`}
          label="First Name"
          placeholder="... first name ..."
          value={personalData.first_name.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              first_name: { ...prev.first_name, text: text || null },
            }))
          }
          onSubmit={() =>
            handleUpdate({
              first_name: {
                ...personalData.first_name,
                text: personalData.first_name.text || null,
              },
            })
          }
          onClear={() =>
            handleUpdate({
              first_name: { ...personalData.first_name, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          disabled={!personalData.first_name.enabled}
        />
      )}

      {/* Last Name */}
      <GenericToggle
        key={`last-name-toggle-${personalData.last_name.enabled}`}
        label="Last Name"
        value={personalData.last_name.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            last_name: { ...personalData.last_name, enabled },
          })
        }
        minW="48"
      />
      {personalData.last_name.enabled && (
        <GenericEditable
          key={`last-name-${resetKey}`}
          label="Last Name"
          placeholder="... last name ..."
          value={personalData.last_name.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              last_name: { ...prev.last_name, text: text || null },
            }))
          }
          onSubmit={() =>
            handleUpdate({
              last_name: {
                ...personalData.last_name,
                text: personalData.last_name.text || null,
              },
            })
          }
          onClear={() =>
            handleUpdate({
              last_name: { ...personalData.last_name, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          disabled={!personalData.last_name.enabled}
        />
      )}

      {/* Email */}
      <GenericToggle
        key={`email-toggle-${personalData.email.enabled}`}
        label="Email Address"
        value={personalData.email.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            email: { ...personalData.email, enabled },
          })
        }
        minW="48"
      />
      {personalData.email.enabled && (
        <GenericEditable
          key={`email-${resetKey}`}
          label="Email"
          placeholder="... email ..."
          value={personalData.email.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              email: { ...prev.email, text: text || null },
            }))
          }
          onSubmit={() => {
            if (validateEmail(personalData.email.text || "")) {
              handleUpdate({
                email: {
                  ...personalData.email,
                  text: personalData.email.text || null,
                },
              });
            }
          }}
          onClear={() =>
            handleUpdate({
              email: { ...personalData.email, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          onBlur={() => {
            if (personalData.email.text) validateEmail(personalData.email.text);
          }}
          error={emailError}
          disabled={!personalData.email.enabled}
        />
      )}

      {/* Phone */}
      <GenericToggle
        key={`phone-toggle-${personalData.phone.enabled}`}
        label="Phone Number"
        value={personalData.phone.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            phone: { ...personalData.phone, enabled },
          })
        }
        minW="48"
      />
      {personalData.phone.enabled && (
        <GenericEditable
          key={`phone-${resetKey}`}
          label="Phone"
          placeholder="... phone ..."
          value={personalData.phone.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              phone: { ...prev.phone, text: text || null },
            }))
          }
          onSubmit={() =>
            handleUpdate({
              phone: {
                ...personalData.phone,
                text: personalData.phone.text || null,
              },
            })
          }
          onClear={() =>
            handleUpdate({
              phone: { ...personalData.phone, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          disabled={!personalData.phone.enabled}
        />
      )}

      {/* Address */}
      <GenericToggle
        key={`address-toggle-${personalData.address.enabled}`}
        label="Residential Address"
        value={personalData.address.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            address: { ...personalData.address, enabled },
          })
        }
        minW="48"
      />
      {personalData.address.enabled && (
        <GenericEditable
          key={`address-${resetKey}`}
          label="Address"
          placeholder="... address ..."
          value={personalData.address.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              address: { ...prev.address, text: text || null },
            }))
          }
          onSubmit={() =>
            handleUpdate({
              address: {
                ...personalData.address,
                text: personalData.address.text || null,
              },
            })
          }
          onClear={() =>
            handleUpdate({
              address: { ...personalData.address, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          disabled={!personalData.address.enabled}
        />
      )}

      {/* LinkedIn profile */}
      <GenericToggle
        key={`linkedin-toggle-${personalData.linkedin_url.enabled}`}
        label="LinkedIn profile"
        value={personalData.linkedin_url.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            linkedin_url: { ...personalData.linkedin_url, enabled },
          })
        }
        minW="48"
      />
      {personalData.linkedin_url.enabled && (
        <GenericEditable
          key={`linkedin-${resetKey}`}
          label="LinkedIn profile"
          placeholder="linkedin.com/in/your-name"
          value={personalData.linkedin_url.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              linkedin_url: { ...prev.linkedin_url, text: text || null },
            }))
          }
          onSubmit={() => {
            const result = validatePersonalLink(
              personalData.linkedin_url.text || "",
              "linkedin",
            );
            if (result.ok) {
              handleUpdate({
                linkedin_url: {
                  ...personalData.linkedin_url,
                  text: result.value || null,
                },
              });
            }
          }}
          onClear={() =>
            handleUpdate({
              linkedin_url: { ...personalData.linkedin_url, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          onBlur={() => {
            if (personalData.linkedin_url.text)
              validatePersonalLink(personalData.linkedin_url.text, "linkedin");
          }}
          error={linkedinUrlError}
          disabled={!personalData.linkedin_url.enabled}
        />
      )}
      {personalData.linkedin_url.enabled &&
        linkedinPreview.ok &&
        linkedinPreview.value && (
          <Text fontSize="xs" color="moveon.text.muted">
            Will print as {linkedinPreview.displayValue}. Opens{" "}
            {linkedinPreview.value}.
          </Text>
        )}

      {/* GitHub profile */}
      <GenericToggle
        key={`github-toggle-${personalData.github_url.enabled}`}
        label="GitHub profile"
        value={personalData.github_url.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            github_url: { ...personalData.github_url, enabled },
          })
        }
        minW="48"
      />
      {personalData.github_url.enabled && (
        <GenericEditable
          key={`github-${resetKey}`}
          label="GitHub profile"
          placeholder="github.com/your-name or @your-name"
          value={personalData.github_url.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              github_url: { ...prev.github_url, text: text || null },
            }))
          }
          onSubmit={() => {
            const result = validatePersonalLink(
              personalData.github_url.text || "",
              "github",
            );
            if (result.ok) {
              handleUpdate({
                github_url: {
                  ...personalData.github_url,
                  text: result.value || null,
                },
              });
            }
          }}
          onClear={() =>
            handleUpdate({
              github_url: { ...personalData.github_url, text: null },
            })
          }
          handleKeyDown={handleKeyDown}
          onBlur={() => {
            if (personalData.github_url.text)
              validatePersonalLink(personalData.github_url.text, "github");
          }}
          error={githubUrlError}
          disabled={!personalData.github_url.enabled}
        />
      )}
      {personalData.github_url.enabled && githubPreview.ok && githubPreview.value && (
        <Text fontSize="xs" color="moveon.text.muted">
          Will print as {githubPreview.displayValue}. Opens {githubPreview.value}.
        </Text>
      )}

      {/* Claim */}
      <GenericToggle
        key={`claim-toggle-${activeLanguage}-${selectedLanguageConfig.claim.enabled}`}
        label="Personal Claim"
        value={selectedLanguageConfig.claim.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            claim: {
              ...personalData.claim,
              [activeLanguage]: {
                ...personalData.claim[activeLanguage],
                enabled,
              },
            },
          })
        }
        minW="48"
      />
      {selectedLanguageConfig.claim.enabled && (
        <GenericEditable
          key={`claim-${activeLanguage}-${resetKey}`}
          label={`Claim (${activeLanguage.toUpperCase()})`}
          placeholder="... claim ..."
          value={selectedLanguageConfig.claim.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              claim: {
                ...prev.claim,
                [activeLanguage]: {
                  ...prev.claim[activeLanguage],
                  text: text || null,
                },
              },
            }))
          }
          onSubmit={() =>
            handleUpdate({
              claim: {
                ...personalData.claim,
                [activeLanguage]: {
                  ...personalData.claim[activeLanguage],
                  text: selectedLanguageConfig.claim.text || null,
                },
              },
            })
          }
          onClear={() =>
            handleUpdate({
              claim: {
                ...personalData.claim,
                [activeLanguage]: {
                  ...personalData.claim[activeLanguage],
                  text: null,
                },
              },
            })
          }
          handleKeyDown={handleKeyDown}
          disabled={!selectedLanguageConfig.claim.enabled}
        />
      )}

      {/* Intro */}
      <GenericToggle
        key={`intro-toggle-${activeLanguage}-${selectedLanguageConfig.intro.enabled}`}
        label="Professional Introduction"
        value={selectedLanguageConfig.intro.enabled || false}
        onChange={(enabled) =>
          handleUpdate({
            intro: {
              ...personalData.intro,
              [activeLanguage]: {
                ...personalData.intro[activeLanguage],
                enabled,
              },
            },
          })
        }
        minW="48"
      />
      {selectedLanguageConfig.intro.enabled && (
        <GenericEditable
          key={`intro-${activeLanguage}-${resetKey}`}
          isTextarea={true}
          label={`Introduction (${activeLanguage.toUpperCase()})`}
          placeholder="... introduction ..."
          value={selectedLanguageConfig.intro.text || ""}
          onChange={(text) =>
            setPersonalData((prev) => ({
              ...prev,
              intro: {
                ...prev.intro,
                [activeLanguage]: {
                  ...prev.intro[activeLanguage],
                  text: text || null,
                },
              },
            }))
          }
          onSubmit={() =>
            handleUpdate({
              intro: {
                ...personalData.intro,
                [activeLanguage]: {
                  ...personalData.intro[activeLanguage],
                  text: selectedLanguageConfig.intro.text || null,
                },
              },
            })
          }
          onClear={() =>
            handleUpdate({
              intro: {
                ...personalData.intro,
                [activeLanguage]: {
                  ...personalData.intro[activeLanguage],
                  text: null,
                },
              },
            })
          }
          handleKeyDown={handleKeyDown}
          disabled={!selectedLanguageConfig.intro.enabled}
        />
      )}
    </VStack>
  );
}
